// PWPOL lane — the forced change-password modal (system-admin F5).
//
// `src/utils/api.js`'s response interceptor calls `requirePasswordChange(payload)` whenever the API
// answers PASSWORD_CHANGE_REQUIRED (a 403 body, or the login body itself). This module keeps a
// singleton gate — parallel 403s collapse into one modal — and the modal resolves it, exactly like
// the operator app's ackGate. Mount <PasswordChangeGate /> once (MainLayout) and it listens.
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Input, List, Modal, Space, Tag, Typography, message } from 'antd';
import { CheckCircle2, Circle, KeyRound, XCircle } from 'lucide-react';
import { passwordPolicyApi, isOk, checkPassword } from '@/utils/passwordPolicyApi';
import { PASSWORD_CHANGE_EVENT, requirePasswordChange, settlePasswordChange } from '@/utils/passwordGate';

const { Text } = Typography;

export { isPasswordChangeRequired, requirePasswordChange } from '@/utils/passwordGate';

export default function PasswordChangeGate() {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState({});
  const [form] = Form.useForm();
  const [pw, setPw] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const onRequired = (e) => {
      setPayload(e.detail || {});
      setError('');
      setPw('');
      form.resetFields();
      setOpen(true);
    };
    window.addEventListener(PASSWORD_CHANGE_EVENT, onRequired);
    return () => window.removeEventListener(PASSWORD_CHANGE_EVENT, onRequired);
  }, [form]);

  const policy = payload.policy || {};
  const username = payload.username || localStorage.getItem('username') || '';
  const checks = checkPassword(pw, policy, username);
  const allOk = checks.every((c) => c.ok !== false);

  const submit = useCallback(
    async (values) => {
      setSaving(true);
      setError('');
      const r = await passwordPolicyApi.change({
        current_password: values.current_password,
        new_password: values.new_password,
        confirm_password: values.confirm_password
      });
      setSaving(false);
      if (!isOk(r)) {
        setError(r?.message || 'Could not change your password.');
        return;
      }
      message.success('Your password has been changed.');
      setOpen(false);
      settlePasswordChange(true);
    },
    []
  );

  const overdue = Number(payload.days_overdue || 0);

  return (
    <Modal
      open={open}
      title={
        <Space>
          <KeyRound size={16} />
          Change your password to continue
        </Space>
      }
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      width={560}
      destroyOnHidden
    >
      <Alert
        type={overdue > 0 ? 'error' : 'warning'}
        showIcon
        style={{ marginBottom: 12 }}
        message={payload.message || 'You must change your password before continuing.'}
        description={
          overdue > 0 ? (
            <Text type="secondary">
              Your password expired {overdue} day(s) ago. Until it is changed only the profile, logout and this screen are available.
            </Text>
          ) : null
        }
      />
      {error && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={error} />}

      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Item name="current_password" label="Current password" rules={[{ required: true, message: 'Required' }]}>
          <Input.Password autoComplete="current-password" />
        </Form.Item>
        <Form.Item name="new_password" label="New password" rules={[{ required: true, message: 'Required' }]}>
          <Input.Password autoComplete="new-password" onChange={(e) => setPw(e.target.value)} />
        </Form.Item>
        <Form.Item
          name="confirm_password"
          label="Confirm new password"
          dependencies={['new_password']}
          rules={[
            { required: true, message: 'Required' },
            ({ getFieldValue }) => ({
              validator: (_, v) => (!v || v === getFieldValue('new_password') ? Promise.resolve() : Promise.reject(new Error('The two passwords do not match')))
            })
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>

        <List
          size="small"
          header={
            <Space>
              <Text strong>Policy rules</Text>
              {policy.role_name ? <Tag color="geekblue">{policy.role_name}</Tag> : null}
              {policy.plant_name ? <Tag>{policy.plant_name}</Tag> : null}
            </Space>
          }
          dataSource={checks}
          renderItem={(c) => (
            <List.Item style={{ padding: '4px 0', border: 0 }}>
              <Space size={6}>
                {c.ok === true ? (
                  <CheckCircle2 size={14} color="#52c41a" />
                ) : c.ok === false ? (
                  <XCircle size={14} color="#ff4d4f" />
                ) : (
                  <Circle size={14} />
                )}
                <Text type={c.ok === false ? 'danger' : undefined}>{c.label}</Text>
              </Space>
            </List.Item>
          )}
        />

        <Space style={{ marginTop: 12 }}>
          <Button type="primary" htmlType="submit" loading={saving} disabled={!allOk || !pw}>
            Change password
          </Button>
        </Space>
      </Form>
    </Modal>
  );
}

/** Small inline banner for the header/profile — shown inside the policy's warn window. */
export function PasswordExpiryBanner() {
  const [state, setState] = useState(null);

  useEffect(() => {
    let alive = true;
    passwordPolicyApi.status().then((r) => {
      if (alive && isOk(r)) setState(r.data);
    });
    return () => {
      alive = false;
    };
  }, []);

  if (!state || !state.in_warn_window || state.must_change_password) return null;
  return (
    <Alert
      banner
      type="warning"
      showIcon
      message={`Your password expires in ${state.password_expires_in_days} day(s).`}
      action={
        <Button size="small" type="link" onClick={() => requirePasswordChange({ policy: state.policy, message: state.warning })}>
          Change now
        </Button>
      }
    />
  );
}
